/**
 * The room's free elements in 3D, built by kind over each one's vertical
 * span. Every element but flooring picks by the box around its span, a named
 * object carries a label just over its top, and the selected element carries
 * the move gadget at its center. Flooring and the bare room floor pick by
 * area instead.
 */
import {
  type CustomAreaType,
  type FloorPlanFreeElement,
  type FloorPlanRoom,
  freeElementRing,
  freeElementSpan,
  isKnownCustomAreaType,
  movesInHeight,
  type Point,
  roomCeilingHeight,
  roomFreeElements,
} from '@kaiizen/kai-floor-plan';
import { PALETTE, type Selection } from '../editor-model';
import { type Vec3 } from '../math3d';
import { wallElementRings } from '../room-geometry';
import { type SceneGadget } from './gadget';
import { ALWAYS, type MeshBuilder, type RGB } from './mesh3d';
import { evenOddContains, type Pickable3D, sameSelection } from './pick3d';
import { buildBox, fillRings, type PlanToWorld } from './sceneBox';

/** How deep a sink's basin is cut into its counter (ft). */
const SINK_BASIN_DEPTH = 8 / 12;
/** The basin floor's thickness (ft). */
const SINK_BASIN_FLOOR = 0.05;
/** How far a sink's top may sit from a counter's top and still be set into it (ft). */
const SINK_TOP_TOLERANCE = 0.5;
/** How far a selected flooring's accent overlay floats above the floor (ft). */
const FLOOR_SELECTION_LIFT = 0.02;
/** How far an object's name label floats above its top (ft). */
const LABEL_LIFT = 0.05;

type Span = [number, number];

/** A sink set into a counter: the counter's index, the sink's footprint, and the counter's top. */
interface SinkBasin {
  counter: number;
  ring: Point[];
  top: number;
}

/** What the free elements add to the scene. */
interface SceneElements {
  labels: { text: string; pos: Vec3 }[];
  pickables: Pickable3D[];
  gadget?: SceneGadget;
}

/** One element as its kind's builder draws it. */
interface ElementBuild {
  mesh: MeshBuilder;
  toWorld: PlanToWorld;
  element: FloorPlanFreeElement;
  index: number;
  ring: Point[];
  span: Span;
  selected: boolean;
  basins: Map<number, SinkBasin>;
  labels: SceneElements['labels'];
}

const elementCenter = (element: FloorPlanFreeElement): Point => ({ x: element.x + element.width / 2, y: element.y + element.length / 2 });
const tint = ({ selected }: ElementBuild, color: RGB): RGB => (selected ? PALETTE.accent : color);
const isSink = (element: FloorPlanFreeElement) => element.customAreaType === 'object' && (element.subtype === 'sink' || element.name === 'sink');

/** A counter: solid up to the floor of the basins set into it, then cut by them to its top. */
function buildCounter(build: ElementBuild): void {
  const { mesh, toWorld, index, ring, span: [base, top], basins } = build;
  const sinks = [...basins.values()].filter(sink => sink.counter === index).map(sink => sink.ring);
  const basinFloor = Math.max(base, top - SINK_BASIN_DEPTH);
  const color = tint(build, PALETTE.counter);
  if (sinks.length) buildBox(mesh, [ring], base, basinFloor, color, toWorld, 'counter');
  buildBox(mesh, [ring, ...sinks], sinks.length ? basinFloor : base, top, color, toWorld, 'counter');
}

/** An interior wall from the floor slab to the ceiling slab: four walls around a void when it is wide enough to hold one. */
function buildWallElement(build: ElementBuild): void {
  const { outer, inner } = wallElementRings(build.element);
  buildBox(build.mesh, inner ? [outer, inner] : [outer], build.span[0], build.span[1], tint(build, PALETTE.wall), build.toWorld, 'wall');
}

/** Flooring paints with the floor (buildFloor); selected, it draws an accent overlay lifted just above it, with a crisp outline. */
function buildFlooring({ mesh, toWorld, ring, selected }: ElementBuild): void {
  if (!selected) return;
  mesh.begin(ALWAYS);
  fillRings(mesh, [ring], FLOOR_SELECTION_LIFT, PALETTE.accent, toWorld);
  mesh.polyline(ring.map(point => toWorld(point.x, point.y, FLOOR_SELECTION_LIFT)), true);
  mesh.end();
}

/** An object is a free box over its span, labeled by its name. A sink set into a counter is that counter's cut instead, floored in the sink's color. */
function buildObject(build: ElementBuild): void {
  const { mesh, toWorld, element, index, ring, span: [base, top], basins, labels } = build;
  const name = element.name || undefined;
  if (basins.has(index)) return buildBox(mesh, [ring], base - SINK_BASIN_FLOOR, base, tint(build, PALETTE.object), toWorld, 'object', ALWAYS, name);
  buildBox(mesh, [ring], base, top, tint(build, PALETTE.object), toWorld, 'object', ALWAYS, name);
  const center = elementCenter(element);
  if (name) labels.push({ text: name, pos: toWorld(center.x, center.y, top + LABEL_LIFT) });
}

/** Each kind's builder. A type from a newer version builds as an object, so it can still be seen and moved. */
const ELEMENT_BUILDERS: Record<CustomAreaType, (build: ElementBuild) => void> = {
  counter: buildCounter,
  flooring: buildFlooring,
  object: buildObject,
  wall: buildWallElement,
};

const builderFor = (element: FloorPlanFreeElement) => (isKnownCustomAreaType(element.customAreaType) ? ELEMENT_BUILDERS[element.customAreaType] : buildObject);

/**
 * Sinks set into a counter, by element index: a sink whose center is over a
 * counter and whose top is within SINK_TOP_TOLERANCE of the counter's top is
 * cut into it as a basin rather than drawn as a box on it.
 */
function sinkBasins(elements: readonly FloorPlanFreeElement[], ceiling: number): Map<number, SinkBasin> {
  const basins = new Map<number, SinkBasin>();
  const topOf = (element: FloorPlanFreeElement) => freeElementSpan(element, ceiling)[1];
  elements.forEach((sink, index) => {
    if (!isSink(sink)) return;
    const center = elementCenter(sink);
    const counter = elements.findIndex(element =>
      element.customAreaType === 'counter' && Math.abs(topOf(sink) - topOf(element)) <= SINK_TOP_TOLERANCE
      && evenOddContains([freeElementRing(element).map(point => [point.x, point.y] as const)], center.x, center.y)
    );
    if (counter >= 0) basins.set(index, { counter, ring: freeElementRing(sink), top: topOf(elements[counter]) });
  });
  return basins;
}

/** The element's pick box: its footprint's plan bounds, conservative for a rotated element, over its span. */
function pickBox({ toWorld, index, ring, span: [bottom, top] }: ElementBuild): Pickable3D {
  const xCoordinates = ring.map(point => point.x), yCoordinates = ring.map(point => point.y);
  return {
    sel: { kind: 'inner', index },
    group: null,
    kind: 'box',
    min: toWorld(Math.min(...xCoordinates), Math.min(...yCoordinates), bottom),
    max: toWorld(Math.max(...xCoordinates), Math.max(...yCoordinates), top),
  };
}

/** The move gadget, standing at the element's center half way up its span; vertical when the element moves in height. */
function elementGadget({ toWorld, element, span: [bottom, top] }: ElementBuild): SceneGadget {
  const center = elementCenter(element);
  return { center: toWorld(center.x, center.y, (bottom + top) / 2), vertical: movesInHeight(element) };
}

/**
 * Builds the room's free elements, each by its kind, leaving out the ones
 * `showElement` hides while every other keeps its index. A sink set into a
 * counter spans its basin, and every other element its own span.
 */
export function buildElements(
  mesh: MeshBuilder,
  shape: FloorPlanRoom,
  toWorld: PlanToWorld,
  selection: Selection | undefined,
  showElement: ((element: FloorPlanFreeElement) => boolean) | undefined,
): SceneElements {
  const elements = roomFreeElements(shape);
  const ceiling = roomCeilingHeight(shape);
  const basins = sinkBasins(elements, ceiling);
  const built: SceneElements = { labels: [], pickables: [] };
  elements.forEach((element, index) => {
    if (showElement && !showElement(element)) return;
    const basin = basins.get(index);
    const span: Span = basin ? [basin.top - SINK_BASIN_DEPTH, basin.top] : freeElementSpan(element, ceiling);
    const selected = !!selection && sameSelection(selection, { kind: 'inner', index });
    const build: ElementBuild = { mesh, toWorld, element, index, ring: freeElementRing(element), span, selected, basins, labels: built.labels };
    builderFor(element)(build);
    if (element.customAreaType !== 'flooring') built.pickables.push(pickBox(build));
    if (selected) built.gadget = elementGadget(build);
  });
  return built;
}

/**
 * The floor pick targets, which pickAt tries only after every solid target:
 * the flooring elements in reverse paint order, so the topmost wins, then the
 * bare room floor, which selects the room. The 2D plan taps in the same order.
 */
export function floorPickables(shape: FloorPlanRoom, roomSolid: Point[][][], toWorld: PlanToWorld): Pickable3D[] {
  const floorRing = (ring: Point[]) => ring.map(point => toWorld(point.x, point.y, 0)).map(([x, , z]) => ({ x, z }));
  const floorings: Pickable3D[] = roomFreeElements(shape).flatMap((element, index) =>
    element.customAreaType === 'flooring' ? [{ sel: { kind: 'inner', index }, group: null, kind: 'floor', rings: [floorRing(freeElementRing(element))] }] : []
  );
  return [...floorings.reverse(), { sel: { kind: 'room' }, group: null, kind: 'floor', rings: roomSolid.flat().map(floorRing) }];
}
